// Lookup editors search another object's records through the list API.
import type { FilterOption } from '@olytic/foundation';

import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import type { ObjectKey } from '../navigation';
import { type ApiFilter, objectListUrl, type ObjectRow, parseObjectListResponse } from './objectsApi';
import type { LoadOptions } from './writes';

/** Up to 20 records of `object` matching the query (and `filters`), labelled as their record pages title them. */
export const loadLookupOptions: LoadOptions = (object: ObjectKey, filters?: ApiFilter[]) => async (query: string): Promise<FilterOption[]> => {
  const page = await parseObjectListResponse(
    await authenticatedFetch(objectListUrl(object, { q: query, ...(filters ? { filters } : {}), limit: 20, offset: 0 })),
  );
  return page.rows.map((row) => ({ value: row.id, label: optionLabel(object, row) }));
};

/** The person's five most recently viewed records of `object`, for a lookup before anything is typed. */
export const loadRecentLookups = (object: ObjectKey) => async (): Promise<FilterOption[]> => {
  const response = await authenticatedFetch(`/api/recent-records?object=${encodeURIComponent(object)}&limit=5`);
  if (!response.ok) return [];
  const body = (await response.json()) as { records?: { id: string; label: string }[] };
  return (body.records ?? []).map((r) => ({ value: r.id, label: r.label }));
};

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** A record's name in a lookup: its title, else first and last name (list rows have no `name` for contacts), else email. */
export function optionLabel(object: ObjectKey, row: ObjectRow): string {
  const title = objects[object]?.titleOf?.(row);
  if (title && title !== '—') return title;
  const person = [text(row.first_name), text(row.last_name)].filter(Boolean).join(' ');
  return person || text(row.name) || text(row.email) || text(row.subject) || row.id;
}
